import { lensStateSchema } from "@repo/addon-contracts/lenses";
import { z } from "zod";

/**
 * The Tier-0 UI-config document model (addon-platform Slice 1).
 *
 * Three shapes, one merge:
 *
 * - **base** — the shipped default configuration, authored in code by the
 *   shell (webapp-v2) and versioned with the app. It enumerates each
 *   configurable surface's item ids in default order, plus lens defaults.
 * - **overlay** — the per-user document persisted server-side. It contains
 *   only deltas, expressed as semantic operations (hide item X, order items
 *   this way, set lens Y), never a copy of the base. Overlays must survive
 *   base upgrades: stale ids are ignored at merge time but preserved in the
 *   stored document, and unknown keys written by newer clients round-trip
 *   through older ones (loose schemas).
 * - **resolved** — merge(base ⊕ overlay): what the shell renders.
 *
 * This merge engine is the seed of the composition-patch engine (research
 * spike addendum: "fork the document, never the code"), which is why the
 * overlay grammar is semantic operations over ids rather than positional or
 * whole-document state.
 */

export const UI_CONFIG_SCHEMA_VERSION = 1;

// ---------------------------------------------------------------------------
// Base (authored in shell code — strict)
// ---------------------------------------------------------------------------

const surfaceItemIdSchema = z.string().min(1);

export const uiConfigBaseSurfaceSchema = z.object({
  /** Item (region/column/widget) ids in default display order. */
  items: z
    .array(surfaceItemIdSchema)
    .refine((items) => new Set(items).size === items.length, {
      message: "base surface items must be unique",
    }),
});

export const uiConfigBaseSchema = z.object({
  schemaVersion: z.literal(UI_CONFIG_SCHEMA_VERSION),
  /** Full lens defaults; every lens the shell supports has a value here. */
  lenses: lensStateSchema.required(),
  surfaces: z.record(z.string().min(1), uiConfigBaseSurfaceSchema),
});

// ---------------------------------------------------------------------------
// Overlay (stored per-user — loose everywhere for forward compatibility)
// ---------------------------------------------------------------------------

export const uiConfigOverlaySurfaceSchema = z.looseObject({
  /**
   * Desired relative order of item ids. May reference ids the current base
   * no longer has (ignored at merge) and omit ids it does have (they keep
   * their base-relative position, anchored to their nearest preceding
   * ordered neighbor).
   */
  order: z.array(surfaceItemIdSchema).optional(),
  /** Item ids the user has hidden. Stale ids are ignored at merge. */
  hidden: z.array(surfaceItemIdSchema).optional(),
});

/**
 * Per-lens overlay values are partial: a user overrides only the fields
 * they changed. Loose so future lens fields written by newer clients
 * survive a read-modify-write cycle through older ones.
 */
export const uiConfigOverlayLensesSchema = z.looseObject({
  privacy: z.looseObject({ enabled: z.boolean().optional() }).optional(),
  denomination: z
    .looseObject({
      currency: z
        .string()
        .regex(/^[A-Z]{3}$/)
        .optional(),
    })
    .optional(),
  density: z
    .looseObject({ mode: z.enum(["comfortable", "compact"]).optional() })
    .optional(),
});

export const uiConfigOverlaySchema = z.looseObject({
  schemaVersion: z.literal(UI_CONFIG_SCHEMA_VERSION),
  lenses: uiConfigOverlayLensesSchema.optional(),
  surfaces: z
    .record(z.string().min(1), uiConfigOverlaySurfaceSchema)
    .optional(),
});

// ---------------------------------------------------------------------------
// Resolved (merge output — strict; what the shell consumes)
// ---------------------------------------------------------------------------

export const resolvedUiConfigSurfaceItemSchema = z.object({
  id: surfaceItemIdSchema,
  hidden: z.boolean(),
});

export const resolvedUiConfigSurfaceSchema = z.object({
  items: z.array(resolvedUiConfigSurfaceItemSchema),
});

export const resolvedUiConfigSchema = z.object({
  lenses: lensStateSchema.required(),
  surfaces: z.record(z.string().min(1), resolvedUiConfigSurfaceSchema),
});

export type UiConfigBaseSurface = z.infer<typeof uiConfigBaseSurfaceSchema>;
export type UiConfigBase = z.infer<typeof uiConfigBaseSchema>;
export type UiConfigOverlaySurface = z.infer<
  typeof uiConfigOverlaySurfaceSchema
>;
export type UiConfigOverlayLenses = z.infer<typeof uiConfigOverlayLensesSchema>;
export type UiConfigOverlay = z.infer<typeof uiConfigOverlaySchema>;
export type ResolvedUiConfigSurfaceItem = z.infer<
  typeof resolvedUiConfigSurfaceItemSchema
>;
export type ResolvedUiConfigSurface = z.infer<
  typeof resolvedUiConfigSurfaceSchema
>;
export type ResolvedUiConfig = z.infer<typeof resolvedUiConfigSchema>;

/** The canonical empty overlay for a user with no customizations. */
export function emptyUiConfigOverlay(): UiConfigOverlay {
  return { schemaVersion: UI_CONFIG_SCHEMA_VERSION };
}
